// Sample two ambient colours from an image for the v2 "artwork-lit" pages.
// Returns { primary: 'r, g, b', secondary: 'r, g, b' } as rgb triples ready for
// rgba(var(--v2-ambient), a). Cached per src. Resolves null when the image
// can't be drawn (CORS without headers, 404) so callers keep the default glow.
const cache = new Map();

function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0; const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h /= 6;
    }
    return [h, s, l];
}
function hslToRgb(h, s, l) {
    const f = (n) => { const k = (n + h * 12) % 12; const a = s * Math.min(l, 1 - l); return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)))); };
    return [f(0), f(8), f(4)];
}

// Remote art has no CORS headers, which taints the canvas and every page fell
// back to the same default glow. The local audio server proxies with
// Access-Control-Allow-Origin: *, so sample through it.
function sampleableUrl(src) {
    if (/^https?:\/\//i.test(src) && !/^https?:\/\/(localhost|127\.0\.0\.1)/i.test(src)) {
        return `http://localhost:3069/stream-proxy?url=${encodeURIComponent(src)}`;
    }
    return src;
}

export function ambientFromImage(src) {
    if (!src) return Promise.resolve(null);
    if (cache.has(src)) return Promise.resolve(cache.get(src));
    const p = new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const S = 24;
                const c = document.createElement('canvas'); c.width = S; c.height = S;
                const ctx = c.getContext('2d', { willReadFrequently: true });
                ctx.drawImage(img, 0, 0, S, S);
                const d = ctx.getImageData(0, 0, S, S).data;
                // Bucket hues (12 buckets), weight by saturation so greys don't win.
                const buckets = Array.from({ length: 12 }, () => ({ w: 0, s: 0, l: 0, n: 0 }));
                let sumS = 0, nS = 0, nWhite = 0, nOpaque = 0;
                for (let i = 0; i < d.length; i += 4) {
                    if (d[i + 3] < 128) continue;
                    nOpaque += 1;
                    const [h, s, l] = rgbToHsl(d[i], d[i + 1], d[i + 2]);
                    if (l > 0.96) { nWhite += 1; continue; }
                    if (l < 0.06) continue;
                    sumS += s; nS += 1;
                    const b = buckets[Math.floor(h * 12) % 12];
                    const w = 0.15 + s;
                    b.w += w; b.s += s * w; b.l += l * w; b.n += 1;
                }
                // A grey picture (a portrait on white, a black-and-white photo) lights a grey room: the saturation floor
                // below scales with the picture's own mean saturation, else the few skin-tone pixels in it won the hue
                // and a white band sat in a brown room (Erez 10-28, Nic Fanciulli).
                const meanS = nS ? sumS / nS : 0;
                const sFloor = Math.min(0.45, meanS * 2.5);
                // A picture on a white backdrop (a studio portrait) lights a pale room: the white pixels are skipped
                // above, so without this the few mid-tone pixels won and a white band sat in a dark halo
                // (Erez 10-29, Sara Landry). lift = 0 below 35% white, 1 at 75%+.
                const whiteFrac = nOpaque ? nWhite / nOpaque : 0;
                const lift = Math.max(0, Math.min(1, (whiteFrac - 0.35) / 0.4));
                const ranked = buckets.map((b, i) => ({ ...b, i })).filter((b) => b.n > 0).sort((a, b) => b.w - a.w);
                if (!ranked.length) { cache.set(src, null); resolve(null); return; }
                const mk = (b) => {
                    const h = (b.i + 0.5) / 12;
                    const s = Math.min(0.85, Math.max(sFloor, b.s / b.w + 0.15)) * (1 - 0.6 * lift);
                    const l0 = Math.min(0.6, Math.max(0.4, b.l / b.w));
                    const l = l0 + (0.88 - l0) * lift;
                    return hslToRgb(h, s, l).join(', ');
                };
                const primary = mk(ranked[0]);
                // Secondary: the strongest bucket at least 3 hues away, else a cool complement.
                const sec = ranked.find((b) => Math.min(Math.abs(b.i - ranked[0].i), 12 - Math.abs(b.i - ranked[0].i)) >= 3);
                const secondary = sec ? mk(sec) : hslToRgb(((ranked[0].i + 6.5) / 12) % 1, 0.5, 0.45).join(', ');
                const out = { primary, secondary };
                cache.set(src, out); resolve(out);
            } catch (e) { cache.set(src, null); resolve(null); }
        };
        img.onerror = () => { cache.set(src, null); resolve(null); };
        img.src = sampleableUrl(src);
    });
    return p;
}

// The element that carries the --v2-ambient vars. On desktop that is the
// react-bootstrap tab pane (it holds exactly one page at a time and is not a
// scroll container, so its glow can bleed past the content gutters); on web it
// is the page's own .v2-ambient element.
export function ambientHost(el) {
    if (!el) return null;
    return el.closest('.tab-content') || el.closest('.v2-ambient') || el;
}
